import React, { useEffect, useRef, useState } from 'react'
import { ChevronUpDownIcon, TruckIcon } from '@heroicons/react/24/outline'
import { useLoadboardTheme } from './loadboardTheme'
import { Label, makeInputBase } from './FilterFields'

const EQUIPMENTS = [
	{ key: 'van', label: 'Dry Van' },
	{ key: 'powerOnly', label: 'Power Only' },
]

function EquipmentDropdown({ value, onChange, flashKey = 0 }) {
	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'
	const inputBase = makeInputBase(tokens)
	const activeItemCls = isLight
		? 'bg-sky-50 text-sky-700'
		: 'bg-sky-500/15 text-sky-200'
	const [isOpen, setIsOpen] = useState(false)
	const [flashing, setFlashing] = useState(false)
	const wrapRef = useRef(null)

	useEffect(() => {
		if (!flashKey) return
		setFlashing(true)
		const t = setTimeout(() => setFlashing(false), 1500)
		return () => clearTimeout(t)
	}, [flashKey])

	useEffect(() => {
		if (!isOpen) return
		const onClick = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [isOpen])

	const selectedKeys = (value || '')
		.split(',')
		.map(s => s.trim())
		.filter(k => EQUIPMENTS.some(eq => eq.key === k))

	const triggerLabel =
		selectedKeys.length === 0
			? 'Select equipment'
			: selectedKeys
					.map(k => EQUIPMENTS.find(e => e.key === k)?.label)
					.filter(Boolean)
					.join(', ')

	const toggleEquipment = key => {
		const set = new Set(selectedKeys)
		if (set.has(key)) set.delete(key)
		else set.add(key)
		onChange(Array.from(set).join(','))
	}

	return (
		<div
			ref={wrapRef}
			className={`w-[260px] relative rounded-md transition-shadow ${
				flashing ? 'ring-2 ring-rose-500 animate-pulse' : ''
			}`}
		>
			<div className={tokens.fieldShell}>
				<Label>Equipment</Label>
				<button
					type="button"
					onClick={() => setIsOpen(v => !v)}
					className={`${inputBase} appearance-none cursor-pointer flex items-center gap-2 text-left pr-5 h-6 leading-6`}
				>
					<TruckIcon className={`h-4 w-4 shrink-0 ${tokens.valueMuted}`} strokeWidth={2} />
					<span className="truncate flex-1">{triggerLabel}</span>
				</button>
				<ChevronUpDownIcon className={`absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none ${tokens.valueMuted}`} />
			</div>

			{isOpen && (
				<div
					className={`absolute left-0 top-full mt-2 z-30 w-48 rounded-md py-1 ${tokens.dropdownBg}`}
				>
					{EQUIPMENTS.map(({ key, label }) => {
						const isChecked = selectedKeys.includes(key)
						return (
							<button
								type="button"
								key={key}
								onClick={() => toggleEquipment(key)}
								className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 ${
									isChecked ? `${activeItemCls} font-medium` : tokens.dropdownItem
								}`}
							>
								<input
									type="checkbox"
									checked={isChecked}
									onChange={() => toggleEquipment(key)}
									onClick={e => e.stopPropagation()}
									className="h-3.5 w-3.5 shrink-0 accent-sky-500 cursor-pointer"
									aria-label={`Select ${label}`}
								/>
								<span>{label}</span>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default EquipmentDropdown
